import { useCallback } from 'react'
import tw from 'twin.macro'
import Image from '@/components/ui/image'
import RichText from '@/components/ui/rich-text'
import { graphql } from 'gatsby'
import { RenderRichTextData, ContentfulRichTextGatsbyReference } from 'gatsby-source-contentful/rich-text'
import colors from '@/constants/colors'
import { ExperienceMapper } from '@ninetailed/experience.js-utils'
import { Experience } from '@ninetailed/experience.js-gatsby'
import { slugify } from '@/utils/strings'
import { extractDataAttributes } from '@/utils/dom'
import Marquee from '@/components/ui/marquee'

export const fragment = graphql`
  fragment SectionSetOfImages on ContentfulSectionSetOfImages {
    __typename
    contentful_id
    internalName
    heading
    body {
      raw
      #  references {
      #    ...Link
      #  }
    }
    imageSize
    images {
      image {
        ...Asset
      }
      link {
        url
      }
    }
    displayAsMarquee
    reverseMarqueeDirection
  }
`
type arrayImg = {
  image: Queries.ContentfulAsset
  link?: { url: string }
}

export interface SetOfImagesProps {
  className?: string
  internalName?: string
  /** Heading - Optional heading displayed above images */
  heading?: string
  body?: RenderRichTextData<ContentfulRichTextGatsbyReference>
  /** Images - Images added via contentful */
  images: arrayImg[]
  /** Image size */
  imageSize?: '32X32' | '64X64' | '96X96' | '128X128' | 'Full'
  /** Page background color */
  pageColor?: string
  pageOrderNo?: number
  displayAsMarquee?: boolean
  reverseMarqueeDirection?: boolean
}

export default function SetOfImages(props): JSX.Element {
  const mapExperiences = useCallback((experiences) => {
    if (!experiences || experiences.length === 0 || !experiences[0]?.variants) return []
    return experiences
      .map(({ variants, ...rest }) => ({
        ...rest,
        variants: variants.map((v) => ({ ...v, id: v.contentful_id, isHero: props.isHero })),
      }))
      .filter(ExperienceMapper.isExperienceEntry)
      .map(ExperienceMapper.mapExperience)
  }, [])

  return (
    <Experience
      {...props}
      id={props.contentful_id ?? props.id}
      key={props.contentful_id ?? props.id}
      experiences={mapExperiences(props.nt_experiences)}
      component={SetOfImagesSection}
      passthroughProps={{ ...props }}
    />
  )
}

/**
 * A set of images
 * - Requires a minimum of 2 images
 * - Has a maximum of 6 images
 */

// eslint-disable-next-line react/function-component-definition
export function SetOfImagesSection({
  className,
  internalName,
  heading,
  body,
  images,
  imageSize,
  pageColor,
  pageOrderNo,
  displayAsMarquee,
  reverseMarqueeDirection,
  ...rest
}: SetOfImagesProps): JSX.Element {
  const dataAttributes = extractDataAttributes(rest)
  const { primaryBlue, indigoBlue } = colors
  const Container = useCallback(
    displayAsMarquee
      ? Marquee
      : ({ children }) => (
          <div
            tw="flex justify-center items-center"
            css={[
              imageSize === '32X32' || imageSize === '64X64'
                ? tw`flex flex-wrap justify-center items-center`
                : tw`grid gap-3 grid-cols-2`,
              images?.length === 4 && tw`sm:grid-cols-4`,
              images?.length === 5 && tw`sm:grid-cols-3 lg:grid-cols-5`,
              images.length === 6 && tw`sm:grid-cols-3 lg:grid-cols-6`,
            ]}
          >
            {children}
          </div>
        ),
    [displayAsMarquee, imageSize, images?.length]
  )
  return images && images.length > 1 ? (
    <section id={slugify(internalName)} {...dataAttributes} className={className}>
      <div tw="mx-auto max-w-8xl px-5 lg:px-9">
        {(heading || body) && (
          <div
            tw="text-center text-indigoBlue font-normal mb-6 md:mb-12"
            css={[(pageColor === indigoBlue || pageColor === primaryBlue) && tw`text-white`]}
          >
            {pageOrderNo === 1 ? <h2 tw="h3">{heading}</h2> : <h3>{heading}</h3>}

            {body && <RichText body={body} />}
          </div>
        )}
        <Container isReversed={reverseMarqueeDirection}>
          {images?.map((image) => (
            <div
              key={image.image?.id || image.image?.contentful_id}
              tw="px-4 flex justify-center items-center"
              css={[
                (imageSize === 'Full' || imageSize === null) && tw`max-w-[220px] [&*]:w-full`,
                imageSize === '32X32' && tw`[&*]:w-[32px]`,
                imageSize === '64X64' && tw`[&*]:w-[64px]`,
                imageSize === '96X96' && tw`[&*]:w-[96px]`,
                imageSize === '128X128' && tw`[&*]:w-[128px]`,
                (imageSize === '32X32' || imageSize === '64X64') && images.length > 4 && tw`px-3 md:px-6 lg:px-10`,
                (imageSize === '32X32' || imageSize === '64X64') && images.length < 3 && tw`px-4 md:px-14 lg:px-28`,
                (imageSize === '32X32' || imageSize === '64X64') && images.length <= 4 && tw`px-4 md:px-10 lg:px-20`,
              ]}
            >
              <Image {...image} />
            </div>
          ))}
        </Container>
      </div>
    </section>
  ) : (
    <div />
  )
}
